import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Stack,
    Textarea,
    useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { useCustomToast } from "components/CustomToast/CustomToast";
import Loading from "components/Loading/Loading";
import SearchSelect from "components/SearchSelect/SearchSelect";
import { ROUTES } from "constants/routes-path";
import { DocumentType, MIN_ELDER_AGE } from "services/documentsParkingCredentialsService";
import { documentsParkingCredentialsService } from "services/documentsParkingCredentialsService";
import { parkingCredentialsService } from "services/parkingCredentialsService";
import { checkErrors, hasError } from "utils/forms-utils";
import { calculateAge } from "utils/utils";
import ListPageCardBeneficiaryHeader from "./ParkingCredentialsDocumentsListPageCardBeneficiaryHeader";

function ParkingCredentialsDocumentsNew() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { id } = useParams();
    const showToast = useCustomToast();

    const textColor = useColorModeValue("gray.700", "white");
    const bgTextarea = useColorModeValue("white", "navy.900");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");

    const { parentId } = location.state || {};
    const [formData, setFormData] = useState(undefined);
    const [typeInvalid, setTypeInvalid] = useState(false);
    const [beneficiaryData, setBeneficiaryData] = useState({});
    const age = useMemo(() => calculateAge(beneficiaryData?.ano_nascimento), [beneficiaryData]);
    const documentTypes = useMemo(() => Object.values(DocumentType), []);
    const [inputError, setInputError] = useState({});

    const ids = useMemo(() => ({
        documentType: "type",
        documentObservations: "observations",
    }), []);

    const cardSpacing = "20px";
    const formSpacing = "30px";
    const formDirection = { sm: "column", md: "row" };

    const validateType = (value) => {
        return !value || (value === DocumentType.ELDER.value && age < MIN_ELDER_AGE);
    };

    const isFormInvalid =
        !formData ||
        validateType(formData[ids.documentType]?.value) ||
        hasError({ formData, inputError });

    const initFormData = (data, documentTypes) => {
        const selectedType = documentTypes.find(dt => dt.display === data?.tipo);
        return {
            [ids.documentType]: {
                value: selectedType ? selectedType.value : "",
                validations: [{ required: true }],
            },
            [ids.documentObservations]: {
                value: data?.observacoes || "",
                validations: [],
            }
        };
    };

    const fetchBeneficiaryData = async () => {
        try {
            const beneficiaryInfo = await parkingCredentialsService.getBeneficiaryByCredentialId(parentId);
            setBeneficiaryData(beneficiaryInfo);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const fetchData = async () => {
        try {
            const beneficiaryInfo = parkingCredentialsService.getBeneficiaryByCredentialId(parentId);
            setBeneficiaryData(beneficiaryInfo);

            if (id) {
                const documentDetails = await documentsParkingCredentialsService.find(parentId, id);
                setFormData(initFormData(documentDetails, documentTypes));
            } else {
                setFormData(initFormData({}, documentTypes));
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const toFormData = () => {
        const formValues = {
            tipoDocumento: formData[ids.documentType].value,
            observacoes: formData[ids.documentObservations].value,
        };

        const data = new FormData();
        data.append("parentID", parentId);
        id ? data.append("documentData", JSON.stringify(formValues)) : data.append("documentData", new Blob([JSON.stringify(formValues)], { type: "application/json" }));

        return data;
    };

    const handleSubmit = async (e) => {
        if (isFormInvalid) return;

        try {
            const data = toFormData();

            if (id) {
                await documentsParkingCredentialsService.update(data, id);
                showToast({ title: t("documents.messages.success.edit"), status: "success" });
            } else {
                await documentsParkingCredentialsService.create(data, parentId);
                showToast({ title: t("documents.messages.success.create"), status: "success" });
            }

            navigate(`${ROUTES.parkingCredentialsDocumentsPath}/?parent_id=${parentId}`)
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const handleInputChange = (e) => {
        const id = e.target.id;
        const value = e.target.value;

        const newFormData = { ...formData, [id]: { ...formData[id], value } };
        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    useEffect(() => {
        fetchBeneficiaryData();
    }, [parentId]);

    return (
        <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "75px" }}>
            <Card mt={"80px"}>
                <ListPageCardBeneficiaryHeader beneficiaryData={beneficiaryData} />
                <ListPageCardHeader title={t(id ? 'documents.form.edit-title' : 'documents.form.title')} />
                <CardBody px="22px">
                    {formData && documentTypes ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl
                                    isRequired
                                    isInvalid={typeInvalid}
                                >
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t("documents.form.fields.type.label")}
                                    </FormLabel>
                                    <HStack align="center" justify="space-between">
                                        <SearchSelect
                                            required
                                            id={ids.documentType}
                                            value={formData[ids.documentType].value}
                                            isDisabled={!documentTypes.length}
                                            items={documentTypes}
                                            valueField="value"
                                            displayField="display"
                                            onChange={(e) => {
                                                handleInputChange(e);
                                                setTypeInvalid(validateType(e.target.value));
                                            }}
                                            onBlur={() => {
                                                setTypeInvalid(validateType(formData[ids.documentType].value));
                                            }}
                                            placeholder={t('documents.form.fields.type.placeholder')}
                                        />
                                    </HStack>
                                    {!formData[ids.documentType].value && typeInvalid && (
                                        <FormErrorMessage>{t("system.messages.errors.required-field")}</FormErrorMessage>
                                    )}
                                    {formData[ids.documentType].value === DocumentType.ELDER.value && age < MIN_ELDER_AGE && typeInvalid && (
                                        <FormErrorMessage>{t("documents.messages.errors.not-elder")}</FormErrorMessage>
                                    )}
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isInvalid={inputError[ids.documentObservations]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("documents.form.fields.observations.label")}
                                    </FormLabel>
                                    <Textarea
                                        minH="120px"
                                        bg={bgTextarea}
                                        id={ids.documentObservations}
                                        value={formData[ids.documentObservations].value}
                                        onChange={handleInputChange}
                                        _placeholder={{ color: placeholderColor }}
                                        placeholder={t('documents.form.fields.observations.placeholder')}
                                        fontSize="xs"
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.documentObservations])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Flex w="100%" justify="space-between">
                                <Button
                                    variant="no-effects"
                                    alignSelf="flex-end"
                                    w="125px"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    onClick={() => navigate(`${ROUTES.parkingCredentialsDocumentsPath}/?parent_id=${parentId}`)}
                                >
                                    {t("system.actions.cancel")}
                                </Button>
                                <Button
                                    w="100px"
                                    variant="dark"
                                    fontSize="sm"
                                    onClick={handleSubmit}
                                    _hover={{
                                        bg: "grey.500",
                                        color: "white"
                                    }}
                                    isDisabled={isFormInvalid}
                                >
                                    {t(id ? 'system.actions.edit' : 'system.actions.save')}
                                </Button>
                            </Flex>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
        </Flex >
    );
};

export default ParkingCredentialsDocumentsNew;
